import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { returnState } from "../lib/returnState";
import { CheckCircle, WarningOctagon } from "@phosphor-icons/react";
import { useWorkflow } from "../workflow/WorkflowContext";
import { getCalcCoverage, getCountryReturnSummary, acknowledgeCoverageGap } from "../lib/api";
import { computeFilingReadiness } from "../lib/filingReadiness";
import { FilingStatusDrawer } from "./FilingStatusDrawer";
import { toast } from "./ui/sonner";
import { BlockingMessageDialog } from "./BlockingMessageDialog";

// v0.5.9f — STAGE5B-DRAWER. Replaces the stacked readiness banners with a
// single slim status row of per-gate chips + a right slide-over drawer that
// carries each gate's full detail. Every chip derives its verdict from the
// SINGLE source (lib/filingReadiness → compute_readiness) so no two
// indicators can contradict (ARCHITECTURE_INVARIANT #4). Light palette is the
// app default; a muted dark variant layers over it via dark: classes.
export const FilingStatusBar = ({ calcId }) => {
  const { t } = useTranslation();
  const { calc, review } = useWorkflow();
  const navigate = useNavigate();
  const location = useLocation();
  const [coverage, setCoverage] = useState(null);
  const [summary, setSummary] = useState(null);
  const [openGate, setOpenGate] = useState(null);
  const [ackError, setAckError] = useState(null);
  // v0.6.9 — found in a sweep after the uploads-list refresh-error fix:
  // this bar is the app's own documented SINGLE SOURCE of filing
  // readiness (ARCHITECTURE_INVARIANT #4), and `load()` re-runs after
  // exactly the operator actions that change it (a typed-in box save
  // dispatches "country-return-updated"; acknowledging a coverage gap
  // dispatches "coverage-updated") — but both fetches were swallowed
  // silently on failure. A transient blip after either action left the
  // operator staring at a stale readiness verdict with zero indication
  // anything was wrong — the exact same shape as the uploads-list bug,
  // here on the ONE indicator this app promises can never contradict
  // itself. `onAckCoverageGap` below already got this treatment in
  // v0.6.8 (see its own comment) for the WRITE; this is the matching
  // fix for the READ that follows it.
  const [stale, setStale] = useState(false);

  const load = useCallback(async () => {
    if (!calcId) return;
    try {
      const [cov, sum] = await Promise.all([
        getCalcCoverage(calcId),
        getCountryReturnSummary(calcId),
      ]);
      setCoverage(cov);
      setSummary(sum);
      setStale(false);
    } catch {
      setStale(true);
    }
  }, [calcId]);

  useEffect(() => {
    if (!calcId) return undefined;
    load();
    window.addEventListener("coverage-updated", load);
    window.addEventListener("country-return-updated", load);
    window.addEventListener("focus", load);
    window.addEventListener("pageshow", load);
    return () => {
      window.removeEventListener("coverage-updated", load);
      window.removeEventListener("country-return-updated", load);
      window.removeEventListener("focus", load);
      window.removeEventListener("pageshow", load);
    };
  }, [calcId, load]);

  if (!calcId) return null;

  const openGaps = coverage?.open_gap_count || 0;
  const needsInput = summary?.needs_input_total || 0;
  const needsInputItems = (summary?.countries || [])
    .flatMap((c) => c.needs_input_pending || []);
  const verdict = computeFilingReadiness({
    calc, review, coverageOpenGaps: openGaps, needsInputPending: needsInput,
  });
  const blockingCount = verdict.blocking.length;

  const gate = (key) => verdict.gates[key];
  const chips = [
    { key: "reconciliation_ok", label: t("ui.filing.status.reconciliation", { defaultValue: "Reconciliation" }) },
    { key: "review_flags_ok", label: t("ui.filing.status.review", { defaultValue: "Review" }) },
    { key: "coverage_ok", label: t("ui.filing.status.coverage", { defaultValue: "Coverage" }) },
  ];

  // chip class: satisfied = neutral grey + teal tick; failed red gate = red;
  // needs-input amber handled separately.
  const chipCls = (ok, severity) => {
    if (ok) {
      return "border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/60 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700/60";
    }
    if (severity === "red") {
      return "border-rose-300 dark:border-rose-700 bg-rose-50 dark:bg-rose-950/40 text-rose-800 dark:text-rose-200 font-medium hover:bg-rose-100 dark:hover:bg-rose-900/50";
    }
    return "border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/40 text-amber-800 dark:text-amber-200 font-medium hover:bg-amber-100 dark:hover:bg-amber-900/50";
  };

  const verdictCls = verdict.ready
    ? "border-teal-600 bg-teal-700 text-white"
    : (verdict.redBlocking.length > 0
        ? "border-rose-500 bg-rose-600 text-white"
        : "border-amber-500 bg-amber-500 text-amber-950");

  return (
    <>
      <section
        data-testid="filing-status-bar"
        data-ready={verdict.ready ? "true" : "false"}
        className="border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 rounded-xl px-4 py-2.5 flex items-center gap-2 flex-wrap"
      >
        <span className="text-[11px] text-slate-400 dark:text-slate-500 mr-1">
          {t("ui.filing.status.label", { defaultValue: "Filing status" })}
        </span>
        {/* Addition #1 — leading verdict chip from compute_readiness. */}
        <span
          data-testid="filing-verdict-chip"
          data-state={verdict.ready ? "ready" : "blocked"}
          className={`text-[12px] inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 ${verdictCls}`}
        >
          {verdict.ready
            ? <CheckCircle size={14} weight="fill" />
            : <WarningOctagon size={14} weight="fill" />}
          {verdict.ready
            ? t("ui.filing.status.ready", { defaultValue: "Ready to file" })
            : t("ui.filing.status.blocked", { count: blockingCount, defaultValue: `Blocked — ${blockingCount} item${blockingCount === 1 ? "" : "s"}` })}
        </span>
        {chips.map(({ key, label }) => {
          const ok = gate(key);
          return (
            <button
              key={key}
              type="button"
              data-testid={`filing-status-chip-${key}`}
              data-state={ok ? "ok" : "action"}
              onClick={() => setOpenGate(key)}
              className={`chip text-[12px] inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 transition-colors ${chipCls(ok, "red")}`}
            >
              {ok
                ? <span className="text-teal-600 dark:text-teal-400">✓</span>
                : <WarningOctagon size={12} weight="fill" />}
              {label}
            </button>
          );
        })}
        {/* needs-input chip — the one amber/actionable gate. */}
        <button
          type="button"
          data-testid="filing-status-chip-needs_input_ok"
          data-state={gate("needs_input_ok") ? "ok" : "action"}
          onClick={() => setOpenGate("needs_input_ok")}
          className={`chip text-[12px] inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 transition-colors ${chipCls(gate("needs_input_ok"), "amber")}`}
        >
          {gate("needs_input_ok")
            ? <><span className="text-teal-600 dark:text-teal-400">✓</span>{t("ui.filing.status.needs_input_ok", { defaultValue: "Inputs entered" })}</>
            : <>⚠ {t("ui.filing.status.needs_input", { count: needsInput, defaultValue: `${needsInput} needs input` })}</>}
        </button>
        {stale && (
          <button
            type="button"
            data-testid="filing-status-stale"
            onClick={load}
            title={t("ui.filing.status.stale_tooltip", {
              defaultValue: "Couldn't refresh filing status after your last change — this may be out of date. Click to retry.",
            })}
            className="text-[11px] inline-flex items-center gap-1 text-amber-700 dark:text-amber-300 hover:underline"
          >
            <WarningOctagon size={12} weight="fill" />
            {t("ui.filing.status.stale", { defaultValue: "May be stale — retry" })}
          </button>
        )}
        <span className="ml-auto text-[11px] text-slate-400 dark:text-slate-500 hidden sm:inline">
          {t("ui.filing.status.hint", { defaultValue: "Click a chip for details →" })}
        </span>
      </section>
      <FilingStatusDrawer
        openGate={openGate}
        onClose={() => setOpenGate(null)}
        verdict={verdict}
        recon={calc?.reconciliation}
        review={review}
        coverage={coverage}
        needsInputItems={needsInputItems}
        onNavigateBox={(it) => {
          setOpenGate(null);
          navigate(`/calc/${calcId}/country-return/${it.country}#box-${it.box_id}`, { state: returnState(location) });
        }}
        onAckCoverageGap={async (gap, note) => {
          try {
            const next = await acknowledgeCoverageGap(calcId, {
              reason_code: gap.reason_code, country: gap.country, note,
            });
            setCoverage(next);
            window.dispatchEvent(new CustomEvent("coverage-updated"));
            toast.success("Coverage gap acknowledged", {
              description: `${gap.country || "OSS"} — recorded in the audit trail.`,
            });
          } catch (e) {
            // v0.6.8 — was toast.error(), an auto-dismissing corner toast
            // for a failed write to a filing-blocking coverage-gap flag.
            // Missing it could leave the operator believing the gap is
            // acknowledged when the write never happened.
            setAckError(e?.response?.data?.detail?.message || "Please try again.");
          }
        }}
      />
      <BlockingMessageDialog
        open={!!ackError} testid="coverage-ack-error" severity="error"
        title={t("ui.filing.coverage.ack_failed", { defaultValue: "Could not acknowledge" })} onOpenChange={(o) => !o && setAckError(null)}
      ><p>{ackError}</p></BlockingMessageDialog>
    </>
  );
};

export default FilingStatusBar;
